import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

// Sentinel for the "Other..." choice; the typed value replaces it on submit.
const OTHER = '__other__';

const TakeExam = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [accessCode, setAccessCode] = useState(searchParams.get('code') || '');
  const [quizInfo, setQuizInfo] = useState(null);
  const [candidateInfo, setCandidateInfo] = useState({
    name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: enter code, 2: enter details/confirm
  const [isInviteCode, setIsInviteCode] = useState(false);
  const [introAnswers, setIntroAnswers] = useState({});
  const [introOther, setIntroOther] = useState({});

  // Auto-check code if provided in URL
  useEffect(() => {
    if (searchParams.get('code')) {
      handleCheckCode(null, searchParams.get('code'));
    }
  }, []);

  const handleCheckCode = async (e, codeOverride = null) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');

    const codeToCheck = codeOverride || accessCode;

    try {
      const response = await api.get(`/exam/access/${codeToCheck}`);
      setQuizInfo(response.data);

      // If this is an invite code, pre-fill candidate info
      if (response.data.is_invite_code) {
        setIsInviteCode(true);
        setCandidateInfo({
          name: response.data.candidate_name,
          email: response.data.candidate_email
        });
      } else {
        setIsInviteCode(false);
      }

      setStep(2);
    } catch (error) {
      // Show schedule-specific error messages
      const errorData = error.response?.data;
      if (errorData?.message) {
        setError(errorData.message);
      } else {
        setError(errorData?.error || 'Invalid access code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        access_code: accessCode
      };

      // Only include candidate info for non-invite codes
      if (!isInviteCode) {
        payload.candidate_name = candidateInfo.name;
        payload.candidate_email = candidateInfo.email;
      }

      const fields = quizInfo.intro_fields || [];
      if (fields.length) {
        const answers = {};
        for (const f of fields) {
          const chosen = introAnswers[f.id];
          const value = chosen === OTHER ? (introOther[f.id] || '') : (chosen || '');
          if (String(value).trim()) answers[f.id] = String(value).trim();
        }
        const missing = fields
          .filter(f => f.is_required && !answers[f.id])
          .map(f => f.label);
        if (missing.length) {
          setError(`Please answer: ${missing.join(', ')}`);
          setLoading(false);
          return;
        }
        payload.intro_answers = answers;
      }

      const response = await api.post('/exam/start', payload);

      // Store attempt data in sessionStorage for the exam page
      sessionStorage.setItem('examData', JSON.stringify(response.data));
      navigate(`/exam/${response.data.attempt_id}`);
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to start exam');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)'
    }}>
      <div className="card" style={{ width: '100%', maxWidth: '500px', boxShadow: '0 4px 20px rgba(249, 115, 22, 0.15)' }}>
        <div className="text-center mb-5">
          <img src="/lp-logo.png" alt="LP Logo" style={{ height: '60px', marginBottom: '16px' }} />
          <h1 style={{ color: '#ea580c', marginBottom: '8px' }}>LP Learning & Assessment Portal</h1>
          <p className="text-muted">Online Exam Portal</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Step 1: Enter Access Code */}
        {step === 1 && (
          <form onSubmit={handleCheckCode}>
            <div className="form-group">
              <label>Enter Access Code</label>
              <input
                type="text"
                value={accessCode}
                onChange={e => setAccessCode(e.target.value.toUpperCase())}
                placeholder="e.g., ABC12345"
                required
                style={{ textTransform: 'uppercase', fontSize: '18px', textAlign: 'center', letterSpacing: '4px' }}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Continue'}
            </button>

            <div className="text-center mt-4" style={{ fontSize: '13px', color: '#9ca3af' }}>
              <a href="/login" style={{ color: '#f97316', textDecoration: 'none' }}>
                Staff Login
              </a>
            </div>
          </form>
        )}

        {/* Step 2: Enter Candidate Details (or confirm for invite codes) */}
        {step === 2 && quizInfo && (
          <div>
            <div style={{
              background: '#f0fdf4',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#065f46', marginBottom: '8px' }}>{quizInfo.title}</h3>
              {quizInfo.description && <p className="text-muted">{quizInfo.description}</p>}
              {quizInfo.session_name && (
                <p style={{ color: '#065f46', fontSize: '14px', marginTop: '8px' }}>
                  Session: {quizInfo.session_name}
                </p>
              )}
              <div className="mt-3" style={{ fontSize: '14px' }}>
                <p><strong>Duration:</strong> {quizInfo.time_limit_minutes} minutes</p>
                <p><strong>Questions:</strong> {quizInfo.question_count}</p>
                <p><strong>Passing Score:</strong> {quizInfo.passing_score}%</p>
              </div>
            </div>

            <form onSubmit={handleStartExam}>
              {isInviteCode ? (
                /* Show pre-filled info for invite codes (read-only) */
                <div style={{
                  background: '#f9fafb',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <p className="text-muted text-sm mb-2">Candidate Information</p>
                  <p><strong>{candidateInfo.name}</strong></p>
                  <p className="text-muted">{candidateInfo.email}</p>
                </div>
              ) : (
                /* Regular quiz code - ask for candidate details */
                <>
                  <div className="form-group">
                    <label>Your Full Name *</label>
                    <input
                      type="text"
                      value={candidateInfo.name}
                      onChange={e => setCandidateInfo({ ...candidateInfo, name: e.target.value })}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Your Email *</label>
                    <input
                      type="email"
                      value={candidateInfo.email}
                      onChange={e => setCandidateInfo({ ...candidateInfo, email: e.target.value })}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </>
              )}

              {/* Extra details this session asks for. Shown before the exam so
                  results can be reported by department, site and so on. */}
              {(quizInfo.intro_fields || []).map(field => (
                <div className="form-group" key={field.id}>
                  <label>
                    {field.label}
                    {field.is_required && <span style={{ color: '#ef4444' }}> *</span>}
                  </label>

                  {field.field_type === 'dropdown' ? (
                    <>
                      <select
                        value={introAnswers[field.id] ?? ''}
                        onChange={e => setIntroAnswers({ ...introAnswers, [field.id]: e.target.value })}
                        required={field.is_required}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        {field.allow_other && <option value={OTHER}>Other...</option>}
                      </select>
                      {field.allow_other && introAnswers[field.id] === OTHER && (
                        <input
                          type="text"
                          style={{ marginTop: '8px' }}
                          value={introOther[field.id] ?? ''}
                          onChange={e => setIntroOther({ ...introOther, [field.id]: e.target.value })}
                          placeholder={`Please specify your ${field.label.toLowerCase()}`}
                          required
                        />
                      )}
                    </>
                  ) : field.field_type === 'textarea' ? (
                    <textarea
                      rows="3"
                      value={introAnswers[field.id] ?? ''}
                      onChange={e => setIntroAnswers({ ...introAnswers, [field.id]: e.target.value })}
                      required={field.is_required}
                    />
                  ) : (
                    <input
                      type="text"
                      value={introAnswers[field.id] ?? ''}
                      onChange={e => setIntroAnswers({ ...introAnswers, [field.id]: e.target.value })}
                      required={field.is_required}
                    />
                  )}
                </div>
              ))}

              <div className="alert alert-info" style={{ fontSize: '14px' }}>
                <strong>Important:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', marginBottom: 0 }}>
                  {quizInfo.instructions ? (
                    /* Custom session instructions */
                    quizInfo.instructions.split('\n').filter(line => line.trim()).map((line, index) => {
                      const trimmedLine = line.trim();
                      // Remove leading dash if present
                      const displayText = trimmedLine.startsWith('-') ? trimmedLine.substring(1).trim() : trimmedLine;
                      return displayText ? <li key={index}>{displayText}</li> : null;
                    })
                  ) : (
                    /* Default instructions */
                    <>
                      <li>Once started, the timer cannot be paused</li>
                      <li>Make sure you have a stable internet connection</li>
                      <li>Do not refresh or close the browser during the exam</li>
                      <li>The assessment must be completed in one sitting; you cannot save and resume later</li>
                      <li>You may review and change your answers before final submission</li>
                      <li>There is no negative marking for incorrect answers</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  style={{ flex: 1 }}
                  onClick={() => {
                    setStep(1);
                    setQuizInfo(null);
                    setIsInviteCode(false);
                    setCandidateInfo({ name: '', email: '' });
                    setIntroAnswers({});
                    setIntroOther({});
                  }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-success btn-lg"
                  style={{ flex: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Starting...' : 'Start Exam'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TakeExam;
